// Per-user, per-team snapshot of TeamDetail so the team page can hydrate
// from cache and paint the chrome (header, roster, captain controls)
// instantly on subsequent visits, instead of flashing a full-page spinner
// every time. Network fetch still happens in the background to refresh the
// payload — the cache is just for the first frame.
//
// Keying: `coravu.teamDetail:{userId}:{teamId}`. Per-user prefix so shared
// browser sign-ins don't clobber each other.

import type { TeamDetail } from "./teamDetail";

const PREFIX = "coravu.teamDetail:";

function key(userId: string, teamId: string): string {
  return `${PREFIX}${userId}:${teamId}`;
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Read the cached payload, or undefined if nothing's been cached for this
 * (user, team) pair yet. Returns the raw TeamDetail shape the team page
 * uses, so it can be set into state directly.
 */
export function readCachedTeamDetail(
  userId: string,
  teamId: string
): TeamDetail | undefined {
  const s = storage();
  if (!s) return undefined;
  try {
    const raw = s.getItem(key(userId, teamId));
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as TeamDetail | null;
    if (!parsed || typeof parsed !== "object") return undefined;
    // Light shape sanity check so we don't paint garbage if the schema
    // ever drifts; if any required field is missing, treat it as a miss
    // and let the network fetch populate fresh data.
    if (
      !parsed.team ||
      !parsed.competition ||
      !Array.isArray(parsed.members)
    ) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

/** Persist the latest TeamDetail payload for this (user, team). */
export function writeCachedTeamDetail(
  userId: string,
  teamId: string,
  detail: TeamDetail
): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key(userId, teamId), JSON.stringify(detail));
  } catch {
    // quota / private mode — ignore
  }
}

/**
 * Drop the cached entry. Call after leaveTeam / kick / delete / membership
 * change so the cached snapshot can't lie next time.
 */
export function clearCachedTeamDetail(userId: string, teamId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(userId, teamId));
  } catch {
    // ignore
  }
}
